import { gql } from 'graphql-request'
import { useQuery, useMutation } from '@hooks/graphql'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/components/ui/button'
import MultiSelect from '@components/Multiselect'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@components/ui/dialog'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form'

const EDUCATORS_QUERY = gql`
  query organization {
    organization {
      id
      educators {
        role
        profile {
          id
          fullName
          email
        }
      }
    }
  }
`

const ADD_COLLABORATORS_TO_CLASSROOM_MUTATION = gql`
  mutation addCollaboratorsToClassroom($input: AddCollaboratorsToClassroomInput!) {
    addCollaboratorsToClassroom(input: $input) {
      success
      errors {
        message
      }
    }
  }
`

const formSchema = z.object({
  collaborators: z.array(z.object({
    value: z.string(),
    label: z.string()
  })).min(1, 'Select at least one collaborator to add to your classroom')
})

const AddCollaboratorsModal = ({
  classroomId,
  closeModal,
  currentCollaborators,
  isOpen,
  classroomOwner: { id: ownerId },
  refetchCollaborators
}) => {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      collaborators: []
    }
  })

  const { data: { organization: { educators = [] } = {} } = {} } = useQuery({
    queryKey: ['educatorsList'],
    gqlQuery: EDUCATORS_QUERY
  })

  const educatorOptions = educators
    .filter(({ profile }) => {
      const alreadyCollaborator = currentCollaborators.some(
        (collaborator) => collaborator.id === profile.id
      )
      return !alreadyCollaborator && profile.id !== ownerId
    })
    .map(({ profile }) => ({ value: profile.id, label: profile.fullName }))

  const handleCloseModal = () => {
    form.reset()
    closeModal()
  }

  const { mutate: addCollaboratorsToClassroom, isPending: sending } = useMutation({
    mutationKey: ['addCollaboratorsToClassroom'],
    gqlMutation: ADD_COLLABORATORS_TO_CLASSROOM_MUTATION,
    onSuccess: () => {
      refetchCollaborators()
      form.reset()
      closeModal()
    }
  })

  const submit = (formData) => {
    const collaboratorIds = formData.collaborators.map(collaborator => collaborator.value)

    addCollaboratorsToClassroom({
      input: {
        classroomId,
        collaboratorIds
      }
    })
  }

  const { errors } = form.formState

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCloseModal()}>
      <DialogContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(submit)}
            className='flex flex-col'
          >
            <DialogHeader>
              <DialogTitle>Add collaborators</DialogTitle>
              <DialogDescription>
                Search educators to add as collaborators in your classroom.
              </DialogDescription>
            </DialogHeader>

            <div className='w-full my-5 space-y-2'>
              <FormField
                control={form.control}
                name='collaborators'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Collaborators</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={educatorOptions}
                        selected={field.value || []}
                        onChange={field.onChange}
                        placeholder='Type to search'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <If condition={errors.general}>
              <p className='text-sm mt-1 text-destructive' role='alert'>
                {errors.general.message}
              </p>
            </If>

            <DialogFooter>
              <Button
                disabled={errors.general || errors.collaborators || sending}
                className='w-fit'
                type='submit'
                loading={sending}
              >
                {sending ? 'Adding...' : 'Add collaborators'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default AddCollaboratorsModal
